'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  LYRRA_A11Y_STORAGE_KEY,
  LYRRA_A11Y_UPDATED_EVENT,
} from '@/hooks/useKeyboardNavigationSetting';
import {
  collectOpenShadowRoots,
  getRangeClientRect,
  resolveComposedTextSelection,
  selectionIsInsideExcludedZone,
} from '@/lib/a11y-composed-selection';

const MIN_CHARS = 2;
const MAX_CHARS = 1500;
const Z_INDEX = 9750;

/**
 * Endpoint backend public (sans auth) qui retourne un MP3 directement.
 * Aucun fichier n'est sauvegardé côté serveur — l'audio est généré à la demande.
 */
const SPEAK_ENDPOINT = '/api/tts/speak-selection';

function readSpeakOnSelectionEnabled(): boolean {
  try {
    const raw = localStorage.getItem(LYRRA_A11Y_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { speakOnSelection?: boolean };
    return Boolean(parsed.speakOnSelection);
  } catch {
    return false;
  }
}

type FloatingPos = { top: number; left: number };
type SpeakStatus = 'idle' | 'loading' | 'playing' | 'error';

/**
 * Synthèse vocale à la demande sur sélection de texte, via Mistral AI.
 * - Voix : Marie - sad (fr/es/it/pt) ou Paul - sad (autres) côté serveur.
 * - Audio généré à chaque clic, jamais persisté côté serveur ni mis en cache local.
 * - Le blob audio est révoqué dès la fin / l'arrêt pour ne rien garder en mémoire.
 */
const SpeakOnSelectionInner: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [enabled, setEnabled] = useState(readSpeakOnSelectionEnabled);
  const [pos, setPos] = useState<FloatingPos | null>(null);
  const [status, setStatus] = useState<SpeakStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const selectedTextRef = useRef<string>('');
  const statusRef = useRef<SpeakStatus>('idle');

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    const sync = () => setEnabled(readSpeakOnSelectionEnabled());
    window.addEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const teardownAudio = useCallback(() => {
    try {
      audioRef.current?.pause();
    } catch {
      /* ignore */
    }
    audioRef.current = null;
    if (objectUrlRef.current) {
      try {
        URL.revokeObjectURL(objectUrlRef.current);
      } catch {
        /* ignore */
      }
      objectUrlRef.current = null;
    }
  }, []);

  const cancelInflight = useCallback(() => {
    try {
      abortRef.current?.abort();
    } catch {
      /* ignore */
    }
    abortRef.current = null;
  }, []);

  const stop = useCallback(() => {
    cancelInflight();
    teardownAudio();
    setStatus('idle');
    setErrorMessage(null);
  }, [cancelInflight, teardownAudio]);

  const hide = useCallback(() => {
    setPos(null);
    selectedTextRef.current = '';
  }, []);

  useEffect(() => {
    if (!enabled) {
      stop();
      hide();
      return;
    }

    const tryShowFromSelection = () => {
      const resolved = resolveComposedTextSelection();
      const text = resolved?.text ?? '';
      if (!text || text.length < MIN_CHARS) {
        if (statusRef.current === 'idle') hide();
        return;
      }
      if (!resolved || selectionIsInsideExcludedZone(resolved.selection)) {
        hide();
        return;
      }
      const rect = getRangeClientRect(resolved.range);
      if (!rect) return;

      selectedTextRef.current = text.slice(0, MAX_CHARS);
      const top = Math.max(8, rect.top - 48);
      const left = Math.min(
        window.innerWidth - 240,
        Math.max(8, rect.left + rect.width / 2 - 120),
      );
      setPos({ top, left });
    };

    const shadowListenerTargets = new Set<ShadowRoot>();
    const bindShadowListeners = () => {
      for (const shadow of collectOpenShadowRoots(document.documentElement)) {
        if (shadowListenerTargets.has(shadow)) continue;
        shadowListenerTargets.add(shadow);
        shadow.addEventListener('pointerup', onPointerUp);
        shadow.addEventListener('keyup', onPointerUp);
      }
    };

    const onPointerUp = () => {
      bindShadowListeners();
      window.setTimeout(tryShowFromSelection, 10);
    };
    const onSelectionChange = () => {
      bindShadowListeners();
      window.setTimeout(tryShowFromSelection, 10);
    };
    const onScroll = () => {
      if (selectedTextRef.current) tryShowFromSelection();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        stop();
        hide();
      }
    };

    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('keyup', onPointerUp);
    document.addEventListener('selectionchange', onSelectionChange);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKeyDown);
    bindShadowListeners();

    const shadowObserver = new MutationObserver(() => bindShadowListeners());
    shadowObserver.observe(document.documentElement, { childList: true, subtree: true });

    return () => {
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('keyup', onPointerUp);
      document.removeEventListener('selectionchange', onSelectionChange);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('keydown', onKeyDown);
      shadowObserver.disconnect();
      for (const shadow of shadowListenerTargets) {
        shadow.removeEventListener('pointerup', onPointerUp);
        shadow.removeEventListener('keyup', onPointerUp);
      }
    };
  }, [enabled, hide, stop]);

  // Cleanup au démontage
  useEffect(
    () => () => {
      cancelInflight();
      teardownAudio();
    },
    [cancelInflight, teardownAudio],
  );

  const speak = useCallback(async () => {
    const text = selectedTextRef.current;
    if (!text) return;

    cancelInflight();
    teardownAudio();
    setErrorMessage(null);
    setStatus('loading');

    const controller = new AbortController();
    abortRef.current = controller;

    const language = (
      i18n.resolvedLanguage ||
      i18n.language ||
      document.documentElement.lang ||
      'fr'
    ).toString();

    try {
      const response = await fetch(SPEAK_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'audio/mpeg',
        },
        body: JSON.stringify({ text, language }),
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'same-origin',
      });

      if (!response.ok) {
        let detail = '';
        try {
          const j = (await response.json()) as { error?: { message?: string } };
          detail = j?.error?.message ?? '';
        } catch {
          /* ignore non-JSON */
        }
        throw new Error(
          detail ||
            t('accessibility.speakSelection.error', 'Lecture indisponible, réessayez plus tard.'),
        );
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        teardownAudio();
        setStatus('idle');
      };
      audio.onerror = () => {
        teardownAudio();
        setStatus('error');
        setErrorMessage(t('accessibility.speakSelection.error', 'Lecture indisponible.'));
      };
      await audio.play();
      setStatus('playing');
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      teardownAudio();
      setStatus('error');
      setErrorMessage(
        (err as Error)?.message ||
          t('accessibility.speakSelection.error', 'Lecture indisponible.'),
      );
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [cancelInflight, teardownAudio, i18n.resolvedLanguage, i18n.language, t]);

  if (!enabled || typeof document === 'undefined') return null;
  if (!pos) return null;

  const labelListen = t('accessibility.speakSelection.listen', 'Écouter la sélection');
  const labelStop = t('accessibility.speakSelection.stop', 'Arrêter la lecture');
  const labelLoading = t('accessibility.speakSelection.loading', 'Génération…');
  const labelRetry = t('accessibility.speakSelection.retry', 'Réessayer');

  const isPlaying = status === 'playing';
  const isLoading = status === 'loading';
  const isError = status === 'error';

  let primaryLabel = labelListen;
  if (isPlaying) primaryLabel = labelStop;
  else if (isLoading) primaryLabel = labelLoading;
  else if (isError) primaryLabel = labelRetry;

  return createPortal(
    <div
      data-a11y-no-speak="true"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: Z_INDEX,
      }}
    >
      <div
        role="region"
        aria-label={t('accessibility.speakSelection.region', 'Lire la sélection à voix haute')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(0, 27, 61,0.96)',
          color: '#fff',
          padding: '6px 10px 6px 8px',
          borderRadius: 999,
          boxShadow: '0 12px 28px rgba(0,0,0,0.32)',
          border: '1px solid rgba(255,255,255,0.12)',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.1,
          userSelect: 'none',
        }}
      >
        <button
          type="button"
          disabled={isLoading}
          aria-busy={isLoading}
          onMouseDown={(e) => e.preventDefault()}
          onClick={isPlaying ? stop : speak}
          aria-label={primaryLabel}
          title={isError && errorMessage ? errorMessage : primaryLabel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px 4px 4px',
            borderRadius: 999,
            background: 'transparent',
            color: '#fff',
            border: 'none',
            cursor: isLoading ? 'progress' : 'pointer',
            font: 'inherit',
            opacity: isLoading ? 0.85 : 1,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: 999,
              background: isPlaying
                ? '#f59e0b'
                : isError
                  ? '#ef4444'
                  : isLoading
                    ? '#94a3b8'
                    : '#3b82f6',
            }}
          >
            {isLoading ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.9s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            ) : isPlaying ? (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="6" width="12" height="12" rx="1.5" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2A4.5 4.5 0 0 0 14 7.97v8.06A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06A7 7 0 0 1 19 12a7 7 0 0 1-5 6.71v2.06A9 9 0 0 0 21 12 9 9 0 0 0 14 3.23z" />
              </svg>
            )}
          </span>
          <span>{primaryLabel}</span>
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            stop();
            hide();
          }}
          aria-label={t('common.close', 'Fermer')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.1)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      {isError && errorMessage && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginTop: 6,
            background: 'rgba(239,68,68,0.96)',
            color: '#fff',
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 500,
            maxWidth: 280,
            textAlign: 'center',
            boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
          }}
        >
          {errorMessage}
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>,
    document.body,
  );
};

export const SpeakOnSelection = React.memo(SpeakOnSelectionInner);
export default SpeakOnSelection;
